import React, { useEffect, useRef, useState } from 'react';
import { images, restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Leaf } from 'lucide-react';

const ProposteEstiveSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { language } = useLanguage();

  const content = {
    it: { subtitle: "Rassegna Autunnale", title: "Selvaggina", cta: "Prenota ora" },
    de: { subtitle: "Herbstmenü", title: "Wildspezialitäten", cta: "Jetzt reservieren" },
    en: { subtitle: "Autumn Selection", title: "Game Specials", cta: "Book now" },
  };
  const t = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="proposte-estive"
      ref={sectionRef}
      data-testid="proposte-estive-section"
      className="py-8 md:py-14 bg-[#2a1810] relative overflow-hidden"
    >
      {/* Animated background glows */}
      <div className="absolute top-0 left-1/4 w-[300px] h-[300px] bg-[#c9a227]/8 rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-[250px] h-[250px] bg-[#8b7119]/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 right-0 w-[200px] h-[200px] bg-[#c9a227]/5 rounded-full blur-[60px] animate-pulse" style={{ animationDelay: '2s' }}></div>

      {/* Decorative corner lines */}
      <div className="absolute top-8 left-8 w-20 h-20 border-t border-l border-[#c9a227]/20"></div>
      <div className="absolute top-8 right-8 w-20 h-20 border-t border-r border-[#c9a227]/20"></div>
      <div className="absolute bottom-8 left-8 w-20 h-20 border-b border-l border-[#c9a227]/20"></div>
      <div className="absolute bottom-8 right-8 w-20 h-20 border-b border-r border-[#c9a227]/20"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Image with decorative frame - wider on desktop */}
        <div className="max-w-sm md:max-w-2xl mx-auto">
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'}`}>
            {/* Outer decorative frame */}
            <div className="relative p-3 md:p-4">
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-[#c9a227]"></div>
              <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-[#c9a227]"></div>
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-[#c9a227]"></div>
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-[#c9a227]"></div>

              {/* Image container with shine effect */}
              <div className="relative overflow-hidden rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                <img
                  src={images.proposteEstive}
                  alt="Rassegna della Selvaggina - Menù Autunnale"
                  data-testid="proposte-estive-image"
                  className="w-full h-auto object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-10 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href={`tel:${restaurantInfo.phone}`}
            data-testid="proposte-estive-cta"
            className="inline-flex items-center gap-2 bg-[#c9a227] text-[#2a1810] px-10 py-4 font-semibold tracking-wide hover:bg-[#e8d48b] transition-all duration-300 hover:shadow-[0_0_25px_rgba(201,162,39,0.4)] hover:scale-105 rounded-lg"
          >
            <Phone className="w-5 h-5" />
            {t.cta}
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProposteEstiveSection;
